import type { QuestionnaireProductionOrActivityRatio } from '../types/questionnaires.ts';
import type { CasOrCategory } from '../types/db.ts';

const DEFAULT_THRESHOLD_PERCENT = 25;

/**
 * Compute the deviation threshold as a decimal (e.g. 0.25 for 25%).
 * Uses the higher of: activity ratio's implied change, or 25%.
 */
export function questionnaireDeviationThresholdPercent(
  productionOrActivityRatio: QuestionnaireProductionOrActivityRatio,
  casOrCategory: null | CasOrCategory,
): number {
  const ratioValues =
    productionOrActivityRatio.isChemicalSpecific && casOrCategory
      ? (productionOrActivityRatio.byChemical?.[casOrCategory] ?? null)
      : productionOrActivityRatio;

  if (!ratioValues) {
    return DEFAULT_THRESHOLD_PERCENT;
  }

  if (ratioValues.type === 'production' || ratioValues.type === 'activity') {
    const prior = typeof ratioValues.priorYearValue === 'number' ? ratioValues.priorYearValue : 0;
    const current = typeof ratioValues.currentYearValue === 'number' ? ratioValues.currentYearValue : 0;
    if (prior > 0) {
      const ratio = current / prior;
      const percentChange = Math.abs(ratio - 1) * 100;
      return Math.max(percentChange, DEFAULT_THRESHOLD_PERCENT);
    }
  }
  return DEFAULT_THRESHOLD_PERCENT;
}

/**
 * Check if a value change from prior to current exceeds the threshold.
 * Returns 'increase' | 'decrease' if it exceeds, null otherwise.
 */
export function questionnaireDetectLargeChange(
  currentValue: number,
  priorValue: number,
  thresholdPercent: number,
): 'increase' | 'decrease' | null {
  if (priorValue === 0) {
    return currentValue !== 0 ? 'increase' : null;
  }

  const percentChange = Math.abs((currentValue - priorValue) / priorValue) * 100;
  if (percentChange <= thresholdPercent) {
    return null;
  }

  return currentValue > priorValue ? 'increase' : 'decrease';
}
